import { ActionTree } from 'vuex';
import ApiUrl from '../../../../api/url';
import { AccountSetState } from './types';
import { RootState } from '../../../types';
import * as types from './mutation-types';
import { isHappenedOtherPartyError } from '../../../error-type';
import { translator } from '../../../translator';
import { AppContext } from '../../../context';
const MessageKey = AppContext.MessageKey;

export const actions: ActionTree<AccountSetState, RootState> = {
    /**
     * 業務システム紐付き業務一覧取得
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} id 業務システムID
     * @param {any} pageNumber ページ番号
     * @param {any} perPage ページ毎の表示件数
     * @param {any} sort ソートするカラムと順番
     * @returns {Promise<void>}
     */
    async search({ dispatch, commit }, { id, pageNumber, perPage, sort } = {}) {
        const payload = {
            page_number: pageNumber,
            per_page: perPage,
            'sort[column]': sort.column,
            'sort[direction]': sort.direction,
        };
        const templateId = id;
        try {
            commit(types.SEARCH_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'get',
                    url: ApiUrl.BusinessSystem.AccountSet.INDEX.replace(':id', templateId.toString()),
                    params: payload,
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.SEARCH_SUCCESS, resData);
            }
        } catch (resData) {
            // アプリケーションエラー時
            commit(types.SEARCH_FAILURE, resData.errors[0]);
        }
    },

    /**
     * 業務システム紐付き業務生成
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} id 業務システムID
     * @param {any} account_set 業務
     * @returns {Promise<void>}
     */
    async create({ dispatch, commit, rootState }, { id, account_set } = {}) {
        try {
            commit(types.CREATE_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'post',
                    url: ApiUrl.BusinessSystem.AccountSet.CREATE.replace(':id', id.toString()),
                    params: { account_set },
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.CREATE_SUCCESS);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.BusinessSystem.AccountSet.CREATED);
                dispatch('toastMessage', `${account_set.name} ${message}`, {
                    root: true,
                });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.CREATE_FAILURE, params);
        }
    },

    /**
     * 業務の詳細情報取得
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} businessSystemId 業務システムID
     * @param {any} accountSetId 業務ID
     */
    async show({ dispatch, commit }, { businessSystemId, accountSetId }) {
        try {
            commit(types.SHOW_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'get',
                    url: ApiUrl.BusinessSystem.AccountSet.SHOW.replace(':id', businessSystemId.toString()).replace(
                        ':account_set_id',
                        accountSetId.toString()
                    ),
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.SHOW_SUCCESS, resData);
            }
        } catch (resData) {
            // アプリケーションエラー時
            commit(types.SHOW_FAILURE, resData.errors[0]);
        }
    },

    /**
     * 取得済み業務の詳細情報をクリアする
     * @param {any} commit Vuex変数
     */
    async clearDetails({ commit }) {
        commit(types.CLEAR_DETAILS);
    },

    /**
     * 業務システム紐付き業務生成
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} businessSystemId 業務システムID
     * @param {any} id 業務ID
     * @param {any} account_set 業務
     * @returns {Promise<void>}
     */
    async update({ dispatch, commit, rootState }, { businessSystemId, id, account_set } = {}) {
        try {
            commit(types.UPDATE_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'put',
                    url: ApiUrl.BusinessSystem.AccountSet.UPDATE.replace(':id', businessSystemId.toString()).replace(
                        ':account_set_id',
                        id.toString()
                    ),
                    params: { account_set },
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.UPDATE_SUCCESS);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.BusinessSystem.AccountSet.UPDATED);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.UPDATE_FAILURE, params);
        }
    },

    /**
     * 業務削除
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {number} businessSystemId 業務システムID
     * @param {array} accountSetId 業務ID
     * @returns {Promise<void>}
     */
    async destroy({ dispatch, commit, rootState }, { businessSystemId, accountSetId } = {}) {
        const payload = {
            account_set_ids: accountSetId,
        };
        try {
            commit(types.DESTROY_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'delete',
                    url: ApiUrl.BusinessSystem.AccountSet.DESTROY.replace(':id', businessSystemId.toString()).replace(
                        ':account_set_id',
                        accountSetId.toString()
                    ),
                    params: payload,
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.DESTROY_SUCCESS);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.BusinessSystem.AccountSet.DESTROYED);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.DESTROY_FAILURE, params);
        }
    },

    /**
     * CSV出力
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {any} businessSystemId 業務システムID
     * @param {any} sort ソート条件
     * @returns {Promise<void>}
     */
    async csv({ dispatch, commit, rootState }, { businessSystemId, sort } = {}) {
        const payload = {
            sort: sort,
        };
        try {
            commit(types.CSV_OUTPUT_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'post',
                    url: ApiUrl.BusinessSystem.AccountSet.CSV_OUTPUT.replace(':id', businessSystemId.toString()),
                    params: payload,
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.CSV_OUTPUT_SUCCESS);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.Csv.OUTPUT);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            commit(types.CSV_OUTPUT_FAILURE, resData.errors[0]);
        }
    },

    /**
     * 画面切り替え時のstoreの消去
     * @param {any} commit Vuex変数
     * @returns {Promise<void>}
     */
    async clearOnBeforeRoute({ commit }) {
        commit(types.CLEAR_ALL);
    },
};
